import React, { useState, useEffect, useRef } from 'react';
import { dashboardAPI, leadsAPI } from '../services/api';
import FilterBar from './FilterBar';
import { Trash2, CheckCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PotentialClients = ({ onNavigateToPreSales, onCloseDeal, onExportExcel, onExportPDF, showExport = false }) => {
  const { isAdmin, user } = useAuth();
  
  // Check if user can delete (admin or Hima Sree/Shree)
  const canDelete = () => {
    if (isAdmin()) return true;
    if (!user?.name) return false;
    const userName = user.name.toLowerCase().trim();
    // Check for "Hima Sree", "Shree", or variations
    return userName.includes('hima') || userName.includes('shree') || userName === 'shree';
  };
  const [potentialClients, setPotentialClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBDE, setSelectedBDE] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [, setBdeUsers] = useState([]);
  const hasLoadedRef = useRef(false);

  // Helper function to normalize URLs
  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed.replace(/^https?:\/\//i, trimmed.toLowerCase().startsWith('https') ? 'https://' : 'http://');
    }
    return 'https://' + trimmed;
  };

  useEffect(() => {
    fetchBDEUsers();
  }, []);

  // Only fetch when filters change, not on every render
  // Only fetch when filters change (not on every render)
  useEffect(() => {
    fetchPotentialClients();
    hasLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBDE, searchTerm, dateFrom, dateTo]);
  
  // Initial load only once
  useEffect(() => {
    if (!hasLoadedRef.current) {
      fetchPotentialClients();
      hasLoadedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBDEUsers = async () => {
    try {
      const result = await dashboardAPI.getBDEUsers();
      if (result.success) {
        setBdeUsers(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching BDE users:', error);
    }
  };

  const fetchPotentialClients = async () => {
    try {
      if (!hasLoadedRef.current) setLoading(true);
      const params = {
        bde: selectedBDE !== 'All' ? selectedBDE : undefined,
        search: searchTerm || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined
      };
      
      console.log('🔄 Fetching potential clients with params:', params);
      const result = await dashboardAPI.getPotentialClients(params);
      console.log('✅ Potential clients result:', result);
      if (result.success) {
        console.log('📊 Potential clients data:', result.data);
        console.log('📊 Number of potential clients:', result.data?.length || 0);
        setPotentialClients(result.data || []);
      }
    } catch (error) {
      console.error('❌ Error fetching potential clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePotentialClient = async (id, companyName) => {
    if (window.confirm(`Are you sure you want to delete "${companyName}" from potential clients?`)) {
      try {
        setLoading(true);
        console.log('🗑️ Attempting to delete potential client:', id, companyName);
        const result = await dashboardAPI.deletePotentialClient(id);
        console.log('✅ Delete result:', result);
        if (result.success) {
          // No alert, just refresh
          fetchPotentialClients();
        } else {
          alert('Error deleting potential client: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('❌ Error deleting potential client:', error);
        console.error('❌ Error response:', error.response?.data);
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
        alert(`Error deleting potential client: ${errorMessage}. Please check if backend is running.`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Expose refresh function globally for Navigation component
  useEffect(() => {
    window.potentialClientsRefresh = fetchPotentialClients;
    // Expose filter values for export
    window.potentialClientsFilters = {
      selectedBDE,
      searchTerm,
      dateFrom,
      dateTo
    };
    return () => {
      delete window.potentialClientsRefresh;
      delete window.potentialClientsFilters;
    };
  }, [selectedBDE, searchTerm, dateFrom, dateTo]);

  return (
    <div className="space-y-4">
      <FilterBar
        selectedDate=""
        setSelectedDate={() => {}}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        selectedBDE={selectedBDE}
        setSelectedBDE={setSelectedBDE}
        selectedStage="All"
        setSelectedStage={() => {}}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showDateRange={true}
        showSearch={true}
        showStageFilter={false}
        onExportExcel={onExportExcel}
        onExportPDF={onExportPDF}
        showExport={showExport}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Potential Clients ({potentialClients.length})</h2>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#679632]" />
          <p className="mt-2 text-gray-500 text-sm">Loading...</p>
        </div>
      ) : potentialClients.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-400 text-sm">No potential clients found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Company</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">POC</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">BDE</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {potentialClients.map((client) => {
                  const rowClick = onNavigateToPreSales && client.source_lead_id ? async (e) => {
                    if (e.target.closest('button') || e.target.closest('a')) return;
                    try {
                      const r = await leadsAPI.getById(client.source_lead_id);
                      if (r.data) onNavigateToPreSales(r.data);
                    } catch (err) { console.error('Error fetching lead:', err); }
                  } : undefined;

                  return (
                    <tr key={client.id} className={`hover:bg-gray-50 ${rowClick ? 'cursor-pointer' : ''}`} onClick={rowClick}>
                      <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">
                        {client.converted_date ? (() => { const [y,m,d] = client.converted_date.split('-').map(Number); return new Date(y,m-1,d).toLocaleDateString('en-US',{month:'2-digit',day:'2-digit',year:'numeric'}); })() : '-'}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-sm font-medium ${rowClick ? 'text-[#679632] hover:underline' : 'text-gray-900'}`}>{client.company_name}</span>
                          {client.company_url && <a href={normalizeUrl(client.company_url)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-blue-600" title="Website"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg></a>}
                          {client.linkedin_url && <a href={normalizeUrl(client.linkedin_url)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-[#0077b5]" title="LinkedIn"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg></a>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="text-sm text-gray-900">{client.poc_name || '-'}</p>
                        {client.poc_email && <p className="text-xs text-gray-500 truncate max-w-[180px]">{client.poc_email}</p>}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-600">{[client.city, client.country].filter(Boolean).join(', ') || '-'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${client.converted_from === 'pre_sales' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                          {client.converted_from === 'pre_sales' ? 'Pre Sales' : 'Post Sales'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-600">{client.bde_name || '-'}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Potential -> Pipeline is the normal next step: they
                              want to sign but it is not signed yet. Closing the
                              deal outright stays available for the rare case
                              where a company signs on the spot. */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm(`Move "${client.company_name}" to Pipeline Clients? Use this when they say they want to sign.`)) {
                                try {
                                  setLoading(true);
                                  const result = await dashboardAPI.convertPotentialToPipeline(client.id, { converted_by: user?.id });
                                  if (result.success) { fetchPotentialClients(); if (window.pipelineClientsRefresh) window.pipelineClientsRefresh(); }
                                  else alert('Error: ' + (result.error || 'Unknown'));
                                } catch (error) { alert('Error moving to pipeline: ' + (error.response?.data?.error || error.message)); }
                                finally { setLoading(false); }
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#679632] text-white rounded text-xs font-medium hover:bg-[#587f2b]"
                            title="They want to sign — move to Pipeline Clients"
                          >
                            <ArrowRight size={13} /> To Pipeline
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm(`Skip Pipeline and onboard "${client.company_name}" directly? Only do this if the contract is already signed.`)) {
                                try {
                                  setLoading(true);
                                  const result = await dashboardAPI.convertPotentialClientToOnboarded(client.id);
                                  if (result.success) { fetchPotentialClients(); if (window.onboardedClientsRefresh) window.onboardedClientsRefresh(); }
                                  else alert('Error: ' + (result.error || 'Unknown'));
                                } catch (error) { alert('Error closing deal: ' + (error.response?.data?.error || error.message)); }
                                finally { setLoading(false); }
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-[#679632] text-[#679632] rounded text-xs font-medium hover:bg-[#f1f7ec]"
                            title="Contract already signed — go straight to Onboarded"
                          >
                            <CheckCircle size={13} /> Onboard
                          </button>
                          {canDelete() && (
                            <button onClick={(e) => { e.stopPropagation(); handleDeletePotentialClient(client.id, client.company_name); }}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PotentialClients;

